/**
 * Voice Recognition Engine
 * Handles speech-to-text with continuous listening
 */

export type VoiceCommand = {
  text: string;
  confidence: number;
  timestamp: number;
};

export type VoiceEventListener = (event: {
  type: 'start' | 'end' | 'result' | 'error' | 'no-speech' | 'language-change';
  data?: any;
}) => void;

interface RecognitionOptions {
  language?: string;
  continuous?: boolean;
  onResult?: (transcript: string, isFinal: boolean) => void;
  onError?: (error: any) => void;
  onEnd?: () => void;
}

class SpeechRecognitionService {
  private recognition: any = null;
  private isListening = false;
  private listeners: VoiceEventListener[] = [];
  private continuous = false;
  private interimResults = true;
  private language = 'en-US';
  private micStream?: MediaStream;
  // Optional callback handlers consumers can register
  private resultHandler?: (transcript: string, isFinal: boolean) => void;
  private errorHandler?: (error: any) => void;
  private endHandler?: () => void;

  constructor() {
    this.initRecognition();
  }

  private initRecognition() {
    // Check for browser support
    const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    
    if (!SpeechRecognition) {
      return;
    }

    this.recognition = new SpeechRecognition();
    this.recognition.continuous = this.continuous;
    this.recognition.interimResults = this.interimResults;
    this.recognition.lang = this.language;
    this.recognition.maxAlternatives = 3;

    // Event handlers
    this.recognition.onstart = () => {
      this.isListening = true;
      this.emit({ type: 'start' });
    };

    this.recognition.onend = () => {
      this.isListening = false;
      this.emit({ type: 'end' });
      if (this.endHandler) {
        try { this.endHandler(); } catch {}
      }
    };

    this.recognition.onresult = (event: any) => {
      const results = event.results;
      const lastResult = results[results.length - 1];
      
      if (lastResult.isFinal) {
        const transcript = lastResult[0].transcript;
        const confidence = lastResult[0].confidence;
        
        this.emit({
          type: 'result',
          data: {
            text: transcript.trim(),
            confidence,
            timestamp: Date.now(),
            isFinal: true,
          },
        });
        if (this.resultHandler) {
          try { this.resultHandler(transcript.trim(), true); } catch {}
        }
      } else {
        // Interim results
        const transcript = lastResult[0].transcript;
        this.emit({
          type: 'result',
          data: {
            text: transcript.trim(),
            confidence: 0,
            timestamp: Date.now(),
            isFinal: false,
          },
        });
        if (this.resultHandler) {
          try { this.resultHandler(transcript.trim(), false); } catch {}
        }
      }
    };

    this.recognition.onerror = (event: any) => {
      console.error('Speech recognition error:', event.error);
      this.emit({ type: 'error', data: event.error });
      if (this.errorHandler) {
        try { this.errorHandler(event.error); } catch {}
      }
    };

    this.recognition.onnomatch = () => {
      this.emit({ type: 'no-speech' });
    };

    this.recognition.onspeechend = () => {
      if (!this.continuous) {
        // Keep recognition session alive to allow follow-up commands
        this.emit({ type: 'no-speech' });
      }
    };
  }

  addEventListener(listener: VoiceEventListener) {
    this.listeners.push(listener);
  }

  removeEventListener(listener: VoiceEventListener) {
    this.listeners = this.listeners.filter(l => l !== listener);
  }

/**
 * Emits an event to all registered listeners
 * @param {Object} event - The event to emit
 * @param {string} event.type - The type of event (start, end, result, error, no-speech, language-change)
 * @param {any} [event.data] - Additional data associated with the event
 */
  private emit(event: { type: 'start' | 'end' | 'result' | 'error' | 'no-speech' | 'language-change'; data?: any }) {
    this.listeners.forEach(listener => listener(event));
  }

  async start(continuous = true, options?: RecognitionOptions) {
    if (!this.recognition) {
      throw new Error('Speech Recognition not supported');
    }

    if (this.isListening) {
      return;
    }

    if (options?.language && options.language !== this.language) {
      this.language = options.language;
      this.recognition.lang = options.language;
      this.emit({ type: 'language-change', data: options.language });
    }

    this.continuous = continuous;
    this.recognition.continuous = continuous;

    // Attach optional handlers via options
    if (options?.onResult) this.resultHandler = options.onResult;
    if (options?.onError) this.errorHandler = options.onError;
    if (options?.onEnd) this.endHandler = options.onEnd;

    try {
      if (!this.micStream && navigator.mediaDevices?.getUserMedia) {
        try {
          this.micStream = await navigator.mediaDevices.getUserMedia({
            audio: {
              noiseSuppression: true,
              echoCancellation: true,
              autoGainControl: true,
            },
          });
        } catch (streamError) {
        }
      }

      this.recognition.start();
    } catch (error) {
      console.error('Failed to start recognition:', error);
      throw error;
    }
  }

  stop() {
    if (!this.recognition || !this.isListening) {
      return;
    }

    try {
      this.recognition.stop();
    } catch (error) {
      console.error('Failed to stop recognition:', error);
    }

    if (this.micStream) {
      this.micStream.getAudioTracks().forEach(track => track.stop());
      this.micStream = undefined;
    }
  }

  abort() {
    if (!this.recognition) {
      return;
    }

    try {
      this.recognition.abort();
      this.isListening = false;
    } catch (error) {
      console.error('Failed to abort recognition:', error);
    }
  }

  isSupported(): boolean {
    return !!this.recognition;
  }

  getIsListening(): boolean {
    return this.isListening;
  }

  // Explicit handler registration for consumers not using start(options)
  onResult(handler: (transcript: string, isFinal: boolean) => void) {
    this.resultHandler = handler;
  }

  onError(handler: (error: any) => void) {
    this.errorHandler = handler;
  }

  onEnd(handler: () => void) {
    this.endHandler = handler;
  }
}

// Singleton instance
export const speechRecognition = new SpeechRecognitionService();
